/**
 * Address Value Object
 * Represents a complete US postal address with validation
 */
export class Address {
  private static readonly US_ZIP_CODE_PATTERN = /^\d{5}(-\d{4})?$/;

  private constructor(
    private readonly streetAddressLine: string,
    private readonly cityName: string,
    private readonly stateCode: string,
    private readonly postalCode: string,
  ) {}

  static fromAddressComponents(
    streetAddressLine: string,
    cityName: string,
    stateCode: string,
    postalCode: string,
  ): Address {
    const normalizedStreetAddress = this.validateAndNormalizeStreetAddress(streetAddressLine);
    const normalizedCityName = this.validateAndNormalizeCityName(cityName);
    const normalizedStateCode = this.validateAndNormalizeStateCode(stateCode);
    const normalizedPostalCode = this.validateAndNormalizePostalCode(postalCode);

    return new Address(normalizedStreetAddress, normalizedCityName, normalizedStateCode, normalizedPostalCode);
  }

  static isValidUsPostalCode(postalCode: string): boolean {
    return Address.US_ZIP_CODE_PATTERN.test(postalCode?.trim() || '');
  }

  getStreetAddressLine(): string {
    return this.streetAddressLine;
  }

  getCityName(): string {
    return this.cityName;
  }

  getStateCode(): string {
    return this.stateCode;
  }

  getPostalCode(): string {
    return this.postalCode;
  }

  formatFullAddress(): string {
    return `${this.streetAddressLine}, ${this.cityName}, ${this.stateCode} ${this.postalCode}`;
  }

  formatMailingLabel(): string {
    return `${this.streetAddressLine}\n${this.cityName}, ${this.stateCode} ${this.postalCode}`;
  }

  isEqualTo(otherAddress: Address): boolean {
    return (
      this.streetAddressLine === otherAddress.streetAddressLine &&
      this.cityName === otherAddress.cityName &&
      this.stateCode === otherAddress.stateCode &&
      this.postalCode === otherAddress.postalCode
    );
  }

  private static validateAndNormalizeStreetAddress(streetAddress: string): string {
    const normalizedStreetAddress = streetAddress?.trim();
    if (!normalizedStreetAddress) {
      throw new Error('Street address is required and cannot be empty');
    }
    return normalizedStreetAddress;
  }

  private static validateAndNormalizeCityName(cityName: string): string {
    const normalizedCityName = cityName?.trim();
    if (!normalizedCityName) {
      throw new Error('City name is required and cannot be empty');
    }
    return normalizedCityName;
  }

  private static validateAndNormalizeStateCode(stateCode: string): string {
    const normalizedStateCode = stateCode?.trim();
    if (!normalizedStateCode) {
      throw new Error('State code is required and cannot be empty');
    }
    return normalizedStateCode;
  }

  private static validateAndNormalizePostalCode(postalCode: string): string {
    const normalizedPostalCode = postalCode?.trim();
    if (!this.isValidUsPostalCode(normalizedPostalCode)) {
      throw new Error('Invalid US postal code format. Expected format: 12345 or 12345-6789');
    }
    return normalizedPostalCode;
  }
}
